/** @jsxImportSource @emotion/react */
import React, { useEffect } from 'react';
import { useGetAuthKeyQuery } from "src/api/auth-token-ApiSlice";
import { createBrowserRouter, RouterProvider, RouteObject } from "react-router";
import NotFoundPage from "./pages/NotFoundPage";
import HomePage from "./pages/HomePage";
import OfferPage from 'src/pages/OfferPage/OfferPage';
import { useGetLanguageMessagesQuery } from "src/api/v1/language-ApiSlice";
import { MuiSandboxPage } from 'src/pages/MuiSandboxPage/MuiSandboxPage';
import { Box, CssBaseline, LinearProgress, ThemeProvider } from '@mui/material';
import { theme } from 'src/theme/mui-theme';
import { css, Global } from '@emotion/react';
import { useAppDispatch } from 'src/app/hooks';
import { projectV2ApiSlice } from 'src/api/v2/project-v2-ApiSlice';
import { AppSnackBar } from 'src/components/AppSnackBar/AppSnackBar';
import { getFrontendURL } from 'src/utils/url';
import { ProjectsPage } from 'src/pages/ProjectsPage/ProjectsPage';
import { WelcomePage } from "src/pages/WelcomePage/WelcomePage";
import { PlannerPage } from "src/pages/PlannerPage/PlannerPage";
import { ProjectSettingsPage } from "src/pages/ProjectSettingsPage/ProjectSettingsPage";
import { ProfileSettingsPage } from "src/pages/ProfileSettingsPage/ProfileSettingsPage";
import { ProjectUsersPage } from "src/pages/ProjectUsersPage/ProjectUsersPage";
import { ProfileCompletionPage } from "src/pages/ProfileCompletionForm/ProfileCompletionPage";
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useZohoChat } from 'src/utils/hooks/useZohoChat';
import { ProductFruits } from 'react-product-fruits';

function App() {

  const dispatch = useAppDispatch();
  dayjs.extend(relativeTime);

  const location = window.location.pathname;
  const isProfileCompletionRoute = location === '/profile-completion-form';

  const {
    data: token,
    error: tokenError,
    isFetching: tokenFetching,
  } = useGetAuthKeyQuery(undefined, {
    skip: isProfileCompletionRoute,
  });

  const {data: languageMessages} = useGetLanguageMessagesQuery(undefined, {
    skip: tokenFetching
  })

  if (token != null) {
    // set authkey for legacy planner API calls
    window.localStorage.setItem('authkey', token.token);
  }

  useEffect(() => {
    if (!isProfileCompletionRoute && token == null && tokenError != null) {
      console.log("Can't get auth token. Redirecting to login page.");
      window.location.replace(getFrontendURL());
    }
  }, [token, tokenError, isProfileCompletionRoute]);

  useZohoChat(token != null && !token.isLocal);

  const productFruits = token?.productFruits;

  useEffect(() => {
    if (token && !isProfileCompletionRoute) {
      dispatch(projectV2ApiSlice.endpoints.getProjectNotifications.initiate());
    }
  }, [token, dispatch, isProfileCompletionRoute]);

  const routes: RouteObject[] = [
    {
      path: '/',
      element: <HomePage/>,
      errorElement: <NotFoundPage/>,
    },
    {
      path: '/offer/:projectId',
      element: <OfferPage/>,
    },
    {
      path: '/welcome/new?',
      element: <WelcomePage/>,
    },
    {
      path: '/projects/:projectId?/new?',
      element: <ProjectsPage/>,
    },
    {
      path: '/projects/:projectId/settings',
      element: <ProjectSettingsPage/>,
    },
    {
      path: '/projects/:projectId/users',
      element: <ProjectUsersPage/>,
    },
    {
      path: '/profile',
      element: <ProfileSettingsPage/>,
    },
    {
      path: '/planner/:projectId',
      element: <PlannerPage/>,
    },
    {
      path: '/profile-completion-form',
      element: <ProfileCompletionPage />,
    },
  ]

  if (token != null && token.yiiEnv !== 'prod') {
    routes.push({
      path: '/mui-sandbox',
      element: <MuiSandboxPage/>,
    })
  }

  const router = createBrowserRouter(routes)
  const shouldShowApp = isProfileCompletionRoute || (token && languageMessages);

  return shouldShowApp ? (
      <>
        {productFruits != null && (
            <ProductFruits
                workspaceCode={productFruits.workspaceCode}
                language={productFruits.language}
                user={{
                  username: productFruits.username,
                  email: productFruits.email,
                  firstname: productFruits.firstname,
                  lastname: productFruits.lastname,
                  signUpAt: productFruits.signUpAt,
                }}
            />
        )}
        <CssBaseline/>
        <Global
            styles={css`
              body, html {
                font-family: Inter, sans-serif;
              }
            `}
        />
        <ThemeProvider theme={theme}>
          <RouterProvider router={router}/>
          <AppSnackBar/>
        </ThemeProvider>
      </>
  ) : (
      <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            width: '100vw',
          }}
      >
        <Box sx={{ width: 300 }}>
          <LinearProgress sx={{ height: 10 }} />
        </Box>
      </Box>
  );
}

export default App;
